import {useRecoilValue} from "recoil";
import {accountSelector, channelSelector} from "../store/selector/selector";
import {SERVICE, canUseService, channelPermissions} from "../libs/permissions";

/**
 * 신청하지 않은 서비스의 화면을 **안내로 대체**한다 (2026-09-22 사용자 지시).
 *
 * ## 왜 메뉴를 감추지 않는가
 *
 * 적립 같은 부가 서비스는 **있다는 것을 고객이 알아야 판매가 된다.**
 * 그래서 사이드바 메뉴는 그대로 두고(`Layout.js`), 들어왔을 때 이 안내로 바꾼다.
 * ⇒ 메뉴 노출 여부와 기능 동작 여부는 **서로 다른 판정**이다. 섞지 말 것.
 *
 * ## 종전 상태
 *
 * 적립 화면 4개에는 아무 판정도 없었다. 신청하지 않은 고객이 들어가면
 *  · '포인트 설정'·'포인트 고객 조회' → 설정이 없어 오류로 튕기고
 *  · '포인트 적립 고객 화면'·'포인트 적립 관리자 화면' → **그냥 떴다.**
 * 고객 눈에는 "되다 만 기능" 이었고, 어느 쪽도 "신청하면 쓸 수 있다" 를 말해주지 않았다.
 *
 * ## 화면 가림은 편의다
 *
 * 실제 차단은 서버가 한다 — `SecurityConfig` 의 `/point/**` → `ServiceAuthorizationManager`.
 * 🔴 그래서 이 컴포넌트를 지우거나 우회해도 API 는 403 이다. 반대로 **여기만 고치고
 *    서버를 안 고치면 아무것도 막히지 않는다.** 둘은 항상 같이 본다.
 *
 * ## `account` 가 비어 있을 때 막지 않는 이유
 *
 * `GET /account` 가 실패하면 `accountSelector` 가 `{}` 를 돌려준다. 그 상태로 "신청이 필요합니다"
 * 를 띄우면 **서버 장애를 '미신청' 으로 오인**시킨다 — 쓰던 고객이 갑자기 서비스를 잃은 것처럼 보인다.
 * 판정이 불확실하면 통과시키고, 막는 일은 서버(403)에 맡긴다.
 */
export const ServiceGuard = ({serviceType, title, description, children}) => {
    const account = useRecoilValue(accountSelector)
    const channel = useRecoilValue(channelSelector)

    // 계정을 못 읽은 상태에서는 판정하지 않는다(위 KDoc 참고).
    if (!account?.id) return children

    if (canUseService(channelPermissions(account, channel), serviceType)) return children

    return <ServiceRequiredNotice title={title} description={description} />
}

/** 신청 안내 화면. 레이아웃 유무와 무관하게 쓰이므로 화면을 가정하지 않는다. */
export const ServiceRequiredNotice = ({title, description}) => (
    <div className="w-full flex items-center justify-center p-8" style={{minHeight: "360px"}}>
        <div className="max-w-[560px] w-full rounded-lg border border-gray-200 bg-white p-8 text-center">
            <div className="text-[18px] font-bold text-gray-800">{title}</div>
            <p className="mt-3 text-sm leading-6 text-gray-600 whitespace-pre-line">{description}</p>
            <p className="mt-5 text-xs text-gray-500">
                이용을 원하시면 오모플레이 담당자 또는 카카오톡 채널로 문의해 주세요.
            </p>
        </div>
    </div>
)

/**
 * 적립(포인트) 서비스 가드.
 *
 * ⚠️ 판정 근거는 `account_permission.service_type = 'Point'` 한 곳이다.
 *    이 값은 2026-09-22 에 **새로 도입**했으므로 이미 적립을 쓰는 채널에 행을 넣는
 *    마이그레이션이 먼저 돌아야 한다. 순서가 바뀌면 운영 중인 매장 화면이 끊긴다
 *    (`_ai-runs/2026-09-22_002_service_permission_gating/002_point_permission_migration.sql`).
 */
export const PointServiceGuard = ({children}) => (
    <ServiceGuard
        serviceType={SERVICE.POINT}
        title="적립 서비스는 별도 신청이 필요합니다"
        description={
            "매장에서 고객 전화번호로 포인트를 적립하고 사용하는 기능입니다.\n" +
            "적립율·등급·유효기간을 설정하고, 적립 고객을 조회할 수 있습니다.\n" +
            "현재 고객사 계정에는 적립 서비스가 신청되어 있지 않습니다."
        }
    >
        {children}
    </ServiceGuard>
)

export default ServiceGuard
